import { Box, Popover } from '@material-ui/core';
import { FeeValue, formatMoney, formatPercent, Money } from '@nemuru-eng/js-api-client';
import { CoverageDetails, Insurance, InsuranceCalculator } from '@nemuru-eng/js-api-client/slim';

import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';

interface InsurancePopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  insurance: Insurance;
  principal: Money;
  term: number;
}

export const InsurancePopover = ({ anchorEl, onClose, insurance, principal, term }: InsurancePopoverProps) => {
  const { getTextIdTranslation: t } = useTranslation();

  const open = Boolean(anchorEl);
  const id = open ? 'insurance-details-popover' : undefined;

  const insuranceCalculator = getInsuranceCalculator(insurance, principal, term);

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        style: {
          padding: 16,
          width: 240,
        },
      }}
    >
      <Box>
        <Font variant="h5" color="title" display="block">
          {t(`INSURANCE_TYPE_${insurance.type}`)}
        </Font>

        <FlexColumn mt={1} gridGap={4} width="100%">
          <FlexRow width="100%">
            <Font variant="body2" display="block">
              <Text tId="INSURANCE_PROVIDER" />
            </Font>
            <Font variant="body2" display="block">
              {t(`INSURANCE_PROVIDER_${insurance.provider}`)}
            </Font>
          </FlexRow>

          <FlexRow width="100%">
            <Font variant="body2" display="block">
              <Text tId="INSURANCE_COST" />
            </Font>
            <Font variant="body2" display="block">
              {formatMoney(insuranceCalculator.insuranceTotalAmount())}
            </Font>
          </FlexRow>

          <FlexRow width="100%">
            <Font variant="body2" display="block">
              <Text tId="INSURANCE_PERCENTAGE" />
            </Font>
            <Font variant="body2" display="block">
              {formatPercent(insurance.cost.value)}
            </Font>
          </FlexRow>
        </FlexColumn>
      </Box>
    </Popover>
  );
};

export const getInsuranceCalculator = (pricingInsurance: Insurance, principal: Money, term: number) => {
  const insurance = {
    ...pricingInsurance,
    rappel: pricingInsurance.rappel as FeeValue,
    coverageDetails: pricingInsurance.coverageDetails as CoverageDetails,
  };

  return new InsuranceCalculator(insurance, principal, term);
};
